import { Fragment, useId, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { translateOptional } from "@/i18n/translate";
import { cn } from "@/lib/utils";
import { Ago, SeverityIcon } from "../../components/primitives";
import { formatDuration, formatMs } from "../../format";
import type { PodMetrics, PodSnapshot } from "../../types";
import { SubHeading } from "../backend/stat-tile";
import { LIMITS } from "../backend/tone";
import { loopMax5m, pcieLink, runsOnCpu } from "./pod-metrics";
import { PodErrors, PodGauges, PodModels, PodProcess, PodRoutes, PodStages } from "./pod-side-blocks";

/**
 * The pod's own view (its GET /metrics, polled by the backend every 15 s): image, GPU, models, pod-side
 * latency and errors up front; stages, event loop, process and host behind "details".
 */
export function PodSide({ pod, color, now }: { pod: PodSnapshot; color: string; now: number }) {
  const { t } = useLocalization();
  const [open, setOpen] = useState(false);
  const detailsId = useId();

  // a backend without the poller sends none of the fields
  if (pod.metrics === undefined && pod.metrics_error === undefined && pod.metrics_at === undefined) return null;
  const metrics = pod.metrics ?? null;
  // "TimeoutError: " (an exception without a message) reads better without the colon
  const error = pod.metrics_error?.replace(/:\s*$/, "") || null;
  const unsupported = error === "unsupported";

  return (
    <section className="-mx-4 flex flex-col gap-4 border-t px-4 pt-4">
      <SubHeading
        actions={
          pod.metrics_at && !error ? (
            <span className="text-[11px] text-muted-foreground" title={t("monitoring.pods.side.pollHint")}>
              {t("monitoring.pods.side.polled")} <Ago ts={pod.metrics_at} />
            </span>
          ) : undefined
        }
      >
        {t("monitoring.pods.side.title")}
      </SubHeading>

      {unsupported ? (
        <p className="text-xs text-muted-foreground">{t("monitoring.pods.side.unsupported")}</p>
      ) : (
        <>
          {error ? (
            <p className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {t("monitoring.pods.side.pollFailed", { error })} · {t("monitoring.pods.side.lastGood")}{" "}
              <Ago ts={pod.metrics_at} fallback={t("monitoring.pods.never")} />
            </p>
          ) : (
            !metrics && <p className="text-xs text-muted-foreground">{t("monitoring.pods.side.waiting")}</p>
          )}
          {metrics && (
            <>
              <ServiceLine metrics={metrics} />
              <Alerts metrics={metrics} />
              {metrics.gpu?.available ? (
                <PodGauges metrics={metrics} color={color} />
              ) : (
                <p className="break-words text-xs text-muted-foreground">
                  {t("monitoring.pods.side.noGpu", { error: metrics.gpu?.error ?? "–" })}
                </p>
              )}
              <PodModels models={metrics.models ?? []} />
              <PodRoutes metrics={metrics} />
              <PodErrors errors={metrics.errors ?? []} now={now} />
              <div className="flex flex-col gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 self-start px-2 text-xs text-muted-foreground"
                  onClick={() => setOpen(!open)}
                  aria-expanded={open}
                  aria-controls={detailsId}
                >
                  <ChevronDown className={cn("mr-1 h-4 w-4 transition-transform", open && "rotate-180")} aria-hidden />
                  {open ? t("monitoring.pods.side.hideDetails") : t("monitoring.pods.side.details")}
                </Button>
                {open && (
                  <div id={detailsId} className="flex flex-col gap-4">
                    <PodStages metrics={metrics} />
                    <PodProcess metrics={metrics} />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

/** image (git), GPU, PCIe link, uptime and readiness in one wrapping line */
function ServiceLine({ metrics }: { metrics: PodMetrics }) {
  const { t } = useLocalization();
  const { service, gpu } = metrics;
  // the best link since the start (the current one trains down at idle); an x8 link where the card
  // supports x16 made pods slow before
  const narrow = !!gpu.pcie_width && !!gpu.pcie_max_width && gpu.pcie_width < gpu.pcie_max_width;
  const items: ReactNode[] = [
    <span key="image">
      {t("monitoring.pods.side.image")} <span className="font-mono text-foreground">{service.image ?? "–"}</span>
      {service.git && <span className="font-mono"> ({service.git.slice(0, 7)})</span>}
    </span>,
  ];
  const name = gpu.name ?? service.gpu_type;
  if (name) items.push(<span key="gpu" className="break-words text-foreground">{name}</span>);
  if (gpu.pcie_gen || gpu.pcie_width) {
    items.push(
      <span key="pcie" className={cn("whitespace-nowrap", narrow && "text-amber-300")} title={t("monitoring.pods.side.pcieHint")}>
        {narrow && <SeverityIcon severity="warning" className="mr-1 inline h-3.5 w-3.5 align-[-3px]" />}
        PCIe{" "}
        <span className={cn("font-mono", !narrow && "text-foreground")}>{pcieLink(gpu.pcie_gen, gpu.pcie_width)}</span>
        {narrow && ` ${t("monitoring.pods.side.pcieOf", { max: `x${gpu.pcie_max_width}` })}`}
      </span>,
    );
  }
  items.push(
    <span key="uptime" className="whitespace-nowrap">
      {t("monitoring.pods.side.uptime", { duration: formatDuration(service.uptime_s) })}
    </span>,
  );
  if (service.ready === false) {
    items.push(
      <span key="ready" className="inline-flex items-center gap-1 whitespace-nowrap text-amber-300">
        <SeverityIcon severity="warning" className="h-3.5 w-3.5" />
        {t("monitoring.pods.ping.notReady")}
      </span>,
    );
  } else if (service.ready_after_s !== null && service.ready_after_s !== undefined) {
    items.push(
      <span key="ready" className="whitespace-nowrap">
        {t("monitoring.pods.side.readyAfter", { duration: formatDuration(service.ready_after_s) })}
      </span>,
    );
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
      {items.map((item, i) => (
        <Fragment key={i}>
          {i > 0 && <span aria-hidden>·</span>}
          {item}
        </Fragment>
      ))}
    </div>
  );
}

/** what the pod-side findings flag: failed preload, nets on the CPU, a blocked event loop */
function Alerts({ metrics }: { metrics: PodMetrics }) {
  const { t } = useLocalization();
  const preload = metrics.service.preload_error;
  const onCpu = (metrics.models ?? [])
    .filter(runsOnCpu)
    .map((model) => translateOptional(`monitoring.pods.side.model.${model.key}`) ?? model.key);
  const lag = loopMax5m(metrics);
  const blocked = lag !== null && lag >= LIMITS.podLoopLagMs;
  if (!preload && !onCpu.length && !blocked) return null;

  return (
    <div className="flex flex-col gap-1.5 text-xs">
      {preload && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-300">
          <div className="mb-1 flex items-center gap-2 font-medium">
            <SeverityIcon severity="critical" className="h-3.5 w-3.5" />
            {t("monitoring.pods.side.preloadFailed")}
          </div>
          <p className="break-words font-mono text-[11px] leading-snug [overflow-wrap:anywhere]">{preload}</p>
        </div>
      )}
      {onCpu.length > 0 && (
        <div className="flex items-start gap-2 text-red-300">
          <SeverityIcon severity="critical" className="mt-px h-3.5 w-3.5" />
          <span className="min-w-0 break-words">{t("monitoring.pods.side.onCpuWarning", { models: onCpu.join(", ") })}</span>
        </div>
      )}
      {blocked && (
        <div className="flex items-start gap-2 text-amber-300">
          <SeverityIcon severity="warning" className="mt-px h-3.5 w-3.5" />
          <span className="min-w-0 break-words">{t("monitoring.pods.side.loopBlocked", { max: formatMs(lag) })}</span>
        </div>
      )}
    </div>
  );
}
